import React from "react"

import PieChart from "shared/ui/Charts/PieChart.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"

import {
  STATISTIC_TYPE,
  LEGEND_POSITION,
} from "types/ReportingDashboard/elements.types"

/**
 * Chart content data structure
 */
interface ChartContent {
  data: Array<{
    statistic: STATISTIC_TYPE
    data: Array<{
      id: string | number | null
      label: string | number | null
      value: number
    }>
  }>
  statisticTypes: STATISTIC_TYPE[]
}

/**
 * Props for PieChartDisplayArea component
 */
export interface PieChartDisplayAreaProps {
  /** Chart content ready for rendering */
  chartContent: ChartContent | null
  /** Chart container ref */
  chartContainerRef: React.RefObject<HTMLDivElement | null>
  /** Current statistic type */
  selectedStatisticType: STATISTIC_TYPE
  /** Legend position setting */
  legendPosition: LEGEND_POSITION
  /** Arc labels visibility setting */
  hideArcLabels: boolean
  /** Chart area height */
  chartAreaHeight: string
  /** Loading state */
  isLoading: boolean
  /** Queued for execution state */
  isQueuedForExecution: boolean
  /** Error state */
  error: string | null
  /** Whether config is initializing */
  isInitializing: boolean
  /** Whether load has been attempted */
  hasAttemptedLoad: boolean
  /** Chart data exists */
  hasChartData: boolean
  /** Refresh handler */
  onRefresh: () => void
}

/**
 * PieChartDisplayArea Component
 *
 * Handles the main chart rendering area for pie chart elements.
 * Shows chart, loading states, error states, and empty states.
 *
 * @param props - Component props
 * @returns JSX element for chart display area
 */
const PieChartDisplayArea: React.FC<PieChartDisplayAreaProps> = ({
  chartContent,
  chartContainerRef,
  selectedStatisticType,
  legendPosition,
  hideArcLabels,
  chartAreaHeight,
  isLoading,
  isQueuedForExecution,
  error,
  isInitializing,
  hasAttemptedLoad,
  hasChartData,
  onRefresh,
}) => {
  return (
    <>
      {/* Error display */}
      {error && !isInitializing && (
        <div
          className="absolute top-2 left-2 right-2 z-10 bg-red-50 border border-red-200 text-red-600 px-2 py-1 rounded-sm text-xs shadow-sm"
          role="alert"
          style={{ top: "2px" }}
        >
          <div className="flex justify-between items-center">
            <p className="truncate mr-1">{error}</p>
            <DataQButton
              onClick={onRefresh}
              size="small"
              className="ml-1 flex-shrink-0"
              type="link"
            >
              Retry
            </DataQButton>
          </div>
        </div>
      )}

      {/* Chart display area */}
      {hasChartData && (
        <div
          className="relative flex-grow"
          ref={chartContainerRef}
          style={{
            width: "100%",
            height: chartAreaHeight,
            boxSizing: "border-box",
            backgroundColor: "#ffffff",
            overflow: "hidden",
          }}
        >
          {chartContent && chartContent.data && chartContent.data.length > 0 ? (
            <PieChart
              data={chartContent.data}
              isChart={true}
              selectedStatistic={selectedStatisticType}
              legendPosition={legendPosition}
              hideArcLabels={hideArcLabels}
            />
          ) : (
            <div className="p-4 text-gray-500 flex items-center justify-center h-full">
              No chart data to display. Check selections or filters.
            </div>
          )}
        </div>
      )}

      {/* Loading overlay */}
      {(isLoading || isQueuedForExecution) && (
        <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center rounded-md z-20">
          <div className="text-blue-500 text-sm bg-blue-50 px-3 py-1 rounded-md shadow-sm">
            {isQueuedForExecution ? "Queued for execution..." : "Loading..."}
          </div>
        </div>
      )}

      {/* Empty state when no data */}
      {!hasChartData &&
        !isLoading &&
        !isQueuedForExecution &&
        !error &&
        !isInitializing &&
        hasAttemptedLoad && (
          <div className="flex-grow flex items-center justify-center h-full bg-gray-50 bg-opacity-80 rounded-md shadow-sm">
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-1">Data needed</p>
              <DataQButton onClick={onRefresh} type="primary" size="small">
                Retry
              </DataQButton>
            </div>
          </div>
        )}
    </>
  )
}

export default PieChartDisplayArea
